<!--
  Draggable palette chip: sidebar field row with kind-colored styling and HTML5 drag payload.
-->
<script setup lang="ts">
import { ref } from 'vue'
import type { DragPayload, PaletteField, PaletteGroupKind } from '../model/types'
import { createDragPayload } from '../model/useDragDrop'

const props = defineProps<{
  field: PaletteField
  /** Palette section kind (own / parent / list) for chip colors. */
  groupKind: PaletteGroupKind
}>()

const emit = defineEmits<{
  dragstart: [payload: DragPayload, event: DragEvent]
  /** Click without drag: focus existing placement on the grid (handled by parent). */
  activate: [field: PaletteField]
}>()

const dragging = ref(false)
/** After a drag, ignore the synthetic click some browsers emit. */
let suppressActivateUntil = 0

const kindClass = {
  own: 'pb-field-chip--own',
  parent: 'pb-field-chip--parent',
  list: 'pb-field-chip--list',
  element: 'pb-field-chip--element',
} as const

function onDragStart(event: DragEvent) {
  dragging.value = true
  const itemType = props.field.isTableEntry ? 'table' : 'field'
  const payload = createDragPayload('palette', itemType, props.field.drag)
  emit('dragstart', payload, event)
}

function onDragEnd() {
  dragging.value = false
  suppressActivateUntil = Date.now() + 300
}

function onClick() {
  if (Date.now() < suppressActivateUntil) return
  emit('activate', props.field)
}
</script>

<template>
  <div
    class="pb-field-chip"
    :class="[
      kindClass[groupKind],
      { 'pb-field-chip--table': field.isTableEntry, 'pb-field-chip--dragging': dragging },
    ]"
    draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @click.stop="onClick"
  >
    {{ field.field?.id }} | {{ field.displayName }}
  </div>
</template>
